import { supabase } from '../supabaseClient'

// Errores de supabase.functions.invoke. En un 4xx/5xx el cuerpo JSON de la funcion viene en error.context, que es un Response
// (functions-js 2.x; no error.context.response). Las funciones del Club responden { error: '<mensaje>' } y a veces details.
// Sin cuerpo legible (red, CORS, una pagina HTML de un 504) queda el mensaje de respaldo. Algunos mensajes del servidor siguen en
// ingles: los conocidos se traducen aqui; cualquier otro texto en ingles cae al mensaje de respaldo (nunca se muestra crudo).
const SESION_EXPIRADA = 'Tu sesión expiró; vuelve a entrar.'
const DEMASIADOS_CORREOS = 'Se enviaron demasiados correos; intenta de nuevo en unos minutos.'
const CORREO_REPETIDO = 'Ese correo ya lo usa otra persona en MYKLUBB.'
const EN_ESPANOL = {
  'Unauthorized': SESION_EXPIRADA,
  'Invalid authentication token': SESION_EXPIRADA,
  // getOrgContext: quien llama ya no esta activo en MYKLUBB (su sesion sigue valida)
  'Staff member not found or inactive': 'Tu acceso a MYKLUBB ya no está activo.',
  'Email already registered in authentication system': 'Ese correo ya tiene una cuenta en MYKLUBB.',
  'Invalid email format': 'El correo no tiene un formato válido.',
  'Staff not found': 'No encontramos a esta persona.',
  'Staff member not found': 'No encontramos a esta persona.',
  'Staff member does not have an authentication user': 'Esta persona todavía no tiene acceso creado.',
  'Failed to update staff role': 'No pudimos actualizar el rol. Intenta de nuevo.',
  'Internal server error': 'Algo falló en el servidor. Intenta de nuevo.',
  // create-staff-auth y resend-staff-invitation, cuando el proveedor de correo frena los envios.
  'Too many requests. Please try again later': DEMASIADOS_CORREOS,
  'Email rate limit exceeded. Please try again later': DEMASIADOS_CORREOS,
}

// Lo que se reconoce aunque no venga exacto: el correo repetido (23505 en staff.email, indice coaches_email_key) y el limite de
// envios del proveedor de correo.
const POR_PATRON = [
  [/duplicate key value.*(email|coaches_email_key)/i, CORREO_REPETIDO],
  [/too many requests|rate limit/i, DEMASIADOS_CORREOS],
]

const POR_DEFECTO = 'No pudimos completar la acción.'

// Un mensaje del servidor se muestra si esta en espanol: acentos o signos del espanol, palabras cortas que el ingles no usa, o
// terminaciones como -cion/-ado ("No autorizado"). Lo demas (ingles, codigos, HTML) no.
const ESPANOL = /[áéíóúñ¿¡]|\b(el|la|los|las|del|de|se|que|en|un|una|su|sus|para|con|por|esta|este|ya|hay|sin|al|lo|puede|pudo|tiene)\b|\w(ción|cion|ado|ada|ido|ida)\b/i
export const pareceEspanol = (texto) => typeof texto === 'string' && ESPANOL.test(texto)

/** La traduccion de un texto en ingles que se conoce (exacto o por patron), o null. */
export const traduccionConocida = (texto) => {
  if (typeof texto !== 'string' || !texto.trim()) return null
  const limpio = texto.trim()
  if (EN_ESPANOL[limpio]) return EN_ESPANOL[limpio]
  const patron = POR_PATRON.find(([re]) => re.test(limpio))
  return patron ? patron[1] : null
}

/** El texto para la pantalla: la traduccion conocida, el mismo texto si ya esta en espanol, o el de respaldo. */
export const paraPantalla = (texto, porDefecto = POR_DEFECTO) => {
  if (typeof texto !== 'string' || !texto.trim()) return porDefecto
  const conocida = traduccionConocida(texto)
  if (conocida) return conocida
  const limpio = texto.trim()
  return pareceEspanol(limpio) ? limpio : porDefecto
}

/**
 * El mensaje de cualquier error (de la base, de una funcion ya leida, de la red) listo para la pantalla. Un 23505 sobre el correo
 * (staff.email es unico en todo MYKLUBB) dice que el correo ya lo usa otra persona, no "intenta de nuevo".
 */
export const mensajeParaPantalla = (error, porDefecto = POR_DEFECTO) => {
  if (error?.code === '23505' && /email/i.test(`${error.message || ''} ${error.details || ''}`)) return CORREO_REPETIDO
  return paraPantalla(error?.message, porDefecto)
}

/**
 * Lee el mensaje real de un error de supabase.functions.invoke.
 * @returns {Promise<{ message: string, status: number|null, cuerpo: object|null }>}
 */
export async function leerErrorDeFuncion(error, porDefecto = POR_DEFECTO) {
  const contexto = error?.context
  const status = contexto?.status ?? null
  let cuerpo = null
  if (contexto && typeof contexto.json === 'function') {
    try {
      const leible = typeof contexto.clone === 'function' ? contexto.clone() : contexto
      cuerpo = await leible.json()
    } catch {
      cuerpo = null
    }
  }
  const delServidor = cuerpo?.error || cuerpo?.message || null
  const message = paraPantalla(typeof delServidor === 'string' ? delServidor : null, porDefecto)
  return { message, status, cuerpo }
}

/**
 * Llama una Edge Function y devuelve { data, error } con error = Error(mensaje real) o null. Un 200 con { error } tambien es error.
 */
export async function invocarFuncion(nombre, body, porDefecto = POR_DEFECTO) {
  try {
    const { data, error } = await supabase.functions.invoke(nombre, { body })
    if (error) {
      const { message, status } = await leerErrorDeFuncion(error, porDefecto)
      const err = new Error(message)
      err.status = status
      return { data: null, error: err }
    }
    if (data?.error) return { data: null, error: new Error(paraPantalla(String(data.error), porDefecto)) }
    return { data, error: null }
  } catch {
    return { data: null, error: new Error(porDefecto) }
  }
}
